import { useState } from 'react';
import { api } from '../api.js';
import { t, wanneer } from '../i18n.js';
import { St } from './Status.jsx';

/**
 * De API-gegevens van een FMC invullen (docs/besluiten/api-gegevens-van-een-bron.md), in drie
 * stappen: het certificaat ophalen (zonder iets te versturen), dat bevestigen, en dan pas aanmelden
 * met precies dat certificaat.
 *
 * - Bij **Bron toevoegen** (`opKlaar`): na een geslaagde test gaan de gegevens mee met het formulier.
 * - Bij **een bestaande bron** (`bronId`): de server test nog een keer en slaat op.
 */
const DOEL = {
  lezen: { titel: 'Leesgebruiker', uitleg: 'Leest de versies van de FMC en de firewalls eronder, voor kwetsbare versies. Een rol die alleen mag lezen is genoeg.' },
  uitrollen: { titel: 'Uitrolgebruiker', uitleg: 'Voor het uitrollen van de syslog-standaard: platformbeleid wijzigen en uitrollen. Leeg laten: dan kan ADTS ZERO niets veranderen op de FMC.' },
  blokkeren: { titel: 'Blokkeergebruiker', uitleg: 'Voor blokkades na goedkeuring: alleen de dynamic objects van ADTS ZERO bijwerken, en het toegangsbeleid lezen. Geen uitrollen.' },
  mnt: { titel: 'MnT-gebruiker', uitleg: 'Zoekt in ISE op wie er nu op een adres zit, als ADTS ZERO het zelf nog niet uit syslog weet. Een beheerder met de rol MnT Admin is genoeg; ADTS ZERO leest alleen.' },
};

export default function ApiGegevens({ doel, standaardUrl = '', vastAdres = false, opKlaar, bronId, sensorId, opBewaard, opAnnuleer }) {
  const [v, zetV] = useState({ url: standaardUrl, username: '', password: '' });
  const [cert, zetCert] = useState(null);
  const [bevestigd, zetBevestigd] = useState(false);
  const [uitkomst, zetUitkomst] = useState(null);
  const [fout, zetFout] = useState(null);
  const [bezig, zetBezig] = useState(false);

  /* Een ander adres is een ander certificaat; een andere gebruiker of ander wachtwoord een nieuwe test. */
  const zet = (k) => (e) => {
    const w = e.target.value;
    zetV((x) => ({ ...x, [k]: w }));
    if (k === 'url') { zetCert(null); zetBevestigd(false); }
    zetUitkomst(null); opKlaar?.(null);
  };
  const doe = async (fn) => {
    zetFout(null); zetBezig(true);
    try { await fn(); } catch (e) { zetFout(t(e.details ? Object.values(e.details)[0] : e.message)); } finally { zetBezig(false); }
  };
  const url = v.url.trim().replace(/\/$/, '');
  const g = { url, username: v.username.trim(), password: v.password, fingerprint: cert?.fingerprint };

  const ise = doel === 'mnt';
  const haalCert = () => doe(async () => { zetBevestigd(false); zetCert((await api.apiCertificaat(url, doel, sensorId)).data); });
  const testen = () => doe(async () => {
    const r = bronId ? await api.zetBronApi(bronId, doel, g) : await api.apiTest(doel, g, sensorId);
    zetUitkomst(r.data);
    if (bronId) opBewaard?.(); else opKlaar?.(g);
  });

  return (
    <div className="api-gegevens">
      <div className="setkop"><b>{t(DOEL[doel].titel)}</b><span>{t(DOEL[doel].uitleg)}</span></div>
      {/* Bij een ISE-node het adres van de node zelf: geen eigen veld. */}
      {vastAdres ? (
        <div className="field"><span>{t('Adres van de ISE-node')}</span><span className="mono">{v.url}</span></div>
      ) : (
        <label className="field"><span>{ise ? t('Adres van de ISE-node') : t('Adres van de FMC')}</span>
          <input className="input mono" value={v.url} onChange={zet('url')} placeholder={ise ? 'https://10.32.200.111' : 'https://10.32.8.225'} autoComplete="off" /></label>
      )}
      <div className="two">
        <label className="field"><span>{t('Gebruiker')}</span>
          <input className="input mono" value={v.username} onChange={zet('username')} autoComplete="off" placeholder={doel === 'lezen' ? 'zero-api' : doel === 'blokkeren' ? 'zero-block' : ise ? 'zero-mnt' : 'zero-deploy'} /></label>
        {/* new-password: de browser vult hier niet het wachtwoord van ZERO zelf in. */}
        <label className="field"><span>{t('Wachtwoord')}</span>
          <input className="input" type="password" value={v.password} onChange={zet('password')} autoComplete="new-password" /></label>
      </div>

      {fout && <div className="melder rood">{fout}</div>}

      {!cert ? (
        <div><button type="button" className="btn" disabled={bezig || !/^https:\/\/./.test(url)} onClick={haalCert}>{t('Certificaat ophalen')}</button></div>
      ) : (
        <div className="callout" style={{ display: 'grid', gap: 6 }}>
          <span>{t('Certificaat van {naam}, uitgegeven door {door}, geldig tot {tot}.', { naam: cert.subject ?? '—', door: cert.issuer ?? '—', tot: cert.validTo ? wanneer(cert.validTo) : '—' })}</span>
          {cert.expired && <St soort="serious">{t('Verlopen')}</St>}
          {!cert.trusted && <span className="soft">{ise ? t('Niet te controleren met de CA’s van ZRO011. Vergelijk de vingerafdruk met het Admin-certificaat van de node in ISE (Administration › System › Certificates) voordat je bevestigt.')
            : t('Niet te controleren met de CA’s van ZRO011. Vergelijk de vingerafdruk met die op de FMC (System › Configuration › HTTPS Certificate) voordat je bevestigt.')}</span>}
          <span className="mono" style={{ fontSize: 12, overflowWrap: 'anywhere' }}>SHA-256 {cert.fingerprint}</span>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="checkbox" checked={bevestigd} onChange={(e) => { zetBevestigd(e.target.checked); zetUitkomst(null); opKlaar?.(null); }} />
            {ise ? t('Dit is het certificaat van deze ISE-node') : t('Dit is het certificaat van deze FMC')}</label>
        </div>
      )}

      {cert && (
        <div className="actions" style={{ paddingTop: 0 }}>
          <button type="button" className="btn primary" disabled={bezig || !bevestigd || !g.username || !g.password} onClick={testen}>
            {bezig ? t('Bezig…') : bronId ? t('Testen en opslaan') : t('Aanmelden testen')}</button>
          {opAnnuleer && <button type="button" className="btn" onClick={opAnnuleer}>{t('Annuleren')}</button>}
        </div>
      )}

      {uitkomst && (
        <div className="callout ok">
          <St soort="good">{bronId ? t('Getest en opgeslagen') : t('Aangemeld')}</St>{' '}
          {ise ? t('ISE {versie}, rol van de node: {node}', { versie: uitkomst.version ?? '?', node: uitkomst.node ?? '?' })
            : t('FMC {versie}, met {n} firewalls: {namen}', { versie: uitkomst.version ?? '?', n: uitkomst.devices.length,
              namen: uitkomst.devices.map((d) => d.name).join(', ') || '—' })}
          {uitkomst.rights?.dynamicObjects !== undefined && (
            <p style={{ margin: '8px 0 0' }}>
              <St soort={uitkomst.rights.dynamicObjects ? 'good' : 'serious'}>{t('Dynamic objects')}</St>{' '}
              <St soort={uitkomst.rights.accessPolicies ? 'good' : 'serious'}>{t('Toegangsbeleid lezen')}</St>
              {(!uitkomst.rights.dynamicObjects || !uitkomst.rights.accessPolicies) && <span className="soft"> {t('Deze gebruiker mag dat niet zien; geef de rol die rechten.')}</span>}
            </p>
          )}
          {uitkomst.rights?.platformSettings !== undefined && (
            <div style={{ marginTop: 6 }}>
              <St soort={uitkomst.rights.platformSettings ? 'good' : 'serious'}>{t('Platformbeleid')}</St>{' '}
              <St soort={uitkomst.rights.deploy ? 'good' : 'serious'}>{t('Uitrollen')}</St>
              {(!uitkomst.rights.platformSettings || !uitkomst.rights.deploy) && <span className="soft"> {t('Deze gebruiker mag dat niet zien; geef de rol die rechten.')}</span>}
            </div>
          )}
          {!bronId && <span className="soft"> {t('Wordt opgeslagen met de bron.')}</span>}
        </div>
      )}
    </div>
  );
}

/** Wat er bij een bron staat, zonder het wachtwoord: dat komt nooit terug. */
export function ApiStand({ g }) {
  if (!g) return <span className="muted">{t('Niet ingesteld')}</span>;
  return (
    <span><span className="mono">{g.username}</span> {t('op')} <span className="mono">{g.url}</span>
      <br /><span className="muted" style={{ fontSize: 12 }}>{t('ingesteld {wanneer}', { wanneer: wanneer(g.setAt) })}{g.setBy && <> · {g.setBy.name || g.setBy.number}</>}
        {' · '}<span className="mono" title={g.fingerprint}>SHA-256 {g.fingerprint.slice(0, 23)}…</span></span></span>
  );
}

/** De gebruikers van een FMC; een ISE-node heeft alleen `mnt`. */
export const DOELEN = ['lezen', 'uitrollen', 'blokkeren'];
